import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, Lock, Unlock, RotateCcw, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, adminDelete } from "../../lib/api";

export default function AdminSecurity() {
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/security/lockouts").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const unlock = async (identifier) => {
    try { await api.post("/admin/security/unlock", { identifier }); toast.success("Compte déverrouillé."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const clearAll = async () => {
    if (!window.confirm("Réinitialiser tout l'historique des tentatives de connexion ?")) return;
    try { if (!await adminDelete("/admin/security/lockouts")) return; toast.success("Historique réinitialisé."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const locked = rows.filter((r) => r.locked);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-surface p-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-red-500/15 blur-[100px]" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-red-400"><ShieldAlert size={14} /> Sécurité</div>
            <h1 className="mt-3 font-display text-3xl font-900 text-white sm:text-4xl">Connexions & verrouillages</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-secondary">
              Protection anti-force brute : un compte est verrouillé 15 minutes après 5 tentatives de connexion échouées.
              Vous pouvez déverrouiller manuellement un accès légitime à tout moment.
            </p>
          </div>
          {rows.length > 0 && (
            <button onClick={clearAll} data-testid="security-clear-all" className="aca-btn-secondary inline-flex items-center gap-2 text-white">
              <RotateCcw size={16} /> Tout réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Section « vue » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-vue">Vue d'ensemble</div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="security-panel-vue">
        <Card icon={Lock} c="#FF453A" l="Comptes verrouillés" v={locked.length} testid="security-stat-locked" />
        <Card icon={Clock} c="#FF9F0A" l="Tentatives suivies" v={rows.length} testid="security-stat-tracked" />
      </div>
      </>

      {/* Section « tentatives » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-tentatives">Tentatives de connexion</div>
      <div className="mt-6 overflow-x-auto rounded-xl border border-white/10" data-testid="security-panel-tentatives">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
            <tr><th className="p-4">Origine (IP · e-mail)</th><th className="p-4">Échecs</th><th className="p-4">Dernière tentative</th><th className="p-4">Statut</th><th className="p-4 text-right">Action</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-ink-tertiary" data-testid="security-empty">Aucune tentative suspecte. Tout est calme.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.identifier} className="border-b border-white/5 hover:bg-surface" data-testid={`security-row-${r.identifier}`}>
                <td className="p-4 font-mono text-xs text-white">{r.identifier}</td>
                <td className="p-4 tabular-nums text-white">{r.count}</td>
                <td className="p-4 text-ink-secondary">{r.last_at ? new Date(r.last_at).toLocaleString("fr-FR") : "—"}</td>
                <td className="p-4">
                  {r.locked
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 text-xs text-red-400"><Lock size={12} /> Verrouillé</span>
                    : <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs text-ink-secondary">Surveillé</span>}
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => unlock(r.identifier)} data-testid={`security-unlock-${r.identifier}`} className="inline-flex items-center gap-1.5 rounded-full border border-cyan/40 px-4 py-2 text-[11px] font-600 uppercase tracking-wide text-cyan transition-colors hover:bg-cyan/10">
                    <Unlock size={13} /> Déverrouiller
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
    </div>
  );
}

function Card({ icon: Icon, c, l, v, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-xl border border-white/10 bg-void p-5" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-0.5" style={{ background: c }} />
      <span className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ background: `${c}1f`, color: c }}><Icon size={20} /></span>
      <div className="mt-4 font-display text-3xl font-900 tabular-nums text-white">{v}</div>
      <div className="mt-1 text-sm text-ink-secondary">{l}</div>
    </motion.div>
  );
}
